/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2021 Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { extend } from 'flarum/common/extend';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import ReviewItem from '../components/ReviewItem';
import type Discussion from 'flarum/common/models/Discussion';
import type Mithril from 'mithril';

/**
 * Resolves a `children[0].attrs.discussion`-style path against a rendered vnode tree,
 * yielding `undefined` at the first missing hop.
 *
 * Mithril types every tree level as `Children`, so no property-access chain narrows further —
 * the walk needs an assertion regardless; confining it here replaced two untyped path-lookup
 * packages (45.5 KiB) this extension used to bundle for it.
 */
function walkVnodePath<T>(root: Mithril.Children, path: string): T | undefined {
	let current: unknown = root;

	for (const key of path.replace(/\[(\d+)]/g, '.$1').split('.')) {
		if (typeof current !== 'object' || current === null) {
			return undefined;
		}

		current = (current as Record<string, unknown>)[key];
	}

	return current as T | undefined;
}

export default function () {
	extend(DiscussionList.prototype, 'view', function (vnode) {
		let items = walkVnodePath<Mithril.Vnode[]>(vnode, 'children[0].children[0].children');
		if (items) {
			items = items.map((item) => {
				const discussion = walkVnodePath<Discussion>(item, 'children[0].attrs.discussion');
				if (discussion?.isReview()) {
					(item.children as Mithril.ChildArray)[0] = <ReviewItem discussion={discussion} />;
				}
				return item;
			});
		}
	});
}
